/**
 * Vista kanban genérica.
 *
 * Columnas por el `group_by` de la metadata, tarjetas con el `title_field` y los
 * `subtitle_fields`, y arrastre entre columnas que persiste el cambio.
 *
 * Dos decisiones que explican la forma del código:
 *
 * 1. **Un solo pedido de tarjetas, no uno por columna.** Los conteos salen de
 *    `read_group` (que ve todo, sin límite) y las tarjetas de un único
 *    `search_read` acotado. Así el tablero abre con una llamada en vez de N, y
 *    mover una tarjeta de columna es cambiar un valor en memoria — sin lo cual
 *    el arrastre se sentiría lento aunque el guardado fuera instantáneo. Cuando
 *    una columna tiene más elementos que los traídos, se dice en la columna.
 * 2. **Arrastrar no puede ser la única forma de mover.** Un tablero que solo
 *    funciona con mouse deja afuera a quien navega por teclado (WCAG 2.1.1), y
 *    en un celular el arrastre horizontal pelea con el scroll. Cada tarjeta
 *    tiene además un selector "Mover a" que hace exactamente lo mismo.
 */

import { useMutation } from '@tanstack/react-query';
import clsx from 'clsx';
import { useMemo, useState, type DragEvent, type ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { FieldDisplay, isMany2One } from '@/components/fields';
import { recordPath } from '@/components/shell/menu-utils';
import { Button, EmptyState, ErrorState } from '@/components/shell/primitives';
import { notify } from '@/components/shell/toast';
import { api } from '@/lib/api';
import type { Domain, JsonValue, ModelSpec, RecordData } from '@/lib/types';
import { AvisoDeBloqueo } from '@/views/MotivoBloqueo';
import { groupKey, splitGroupBy, useColumnSkeleton, useGroupLabels } from '@/views/grouping';
import { useInvalidateModel, useReadGroupQuery, useRecordsQuery } from '@/views/queries';
import { SearchPanel, useSearchState } from '@/views/SearchPanel';
import { andDomains, existingFields, parseOrder, serializeOrder } from '@/views/view-utils';

/** Tope de tarjetas traídas para todo el tablero. Ver el comentario de arriba. */
const TOPE_TARJETAS = 200;

export interface KanbanViewProps {
  spec: ModelSpec;
  /** Dominio del menú/acción de origen. Se aplica siempre, en AND con la búsqueda. */
  baseDomain?: Domain;
  /** Menú de origen, para que abrir una tarjeta no pierda el contexto. */
  menuName?: string | null;
  modeSwitcher?: ReactNode;
}

/** Valor de agrupación de una fila, normalizado a algo comparable. */
function valorDeGrupo(fila: RecordData, campo: string): JsonValue {
  const bruto = fila[campo];
  if (bruto === undefined || bruto === null) return null;
  if (isMany2One(bruto)) return bruto.id;
  if (Array.isArray(bruto)) return null;
  return bruto;
}

export function KanbanView({ spec, baseDomain, menuName = null, modeSwitcher }: KanbanViewProps) {
  const navegar = useNavigate();
  const invalidar = useInvalidateModel();
  const busqueda = useSearchState(spec);

  const vista = spec.views.kanban;
  const campoTitulo = vista?.title_field ?? spec.rec_name;
  const subtitulos = existingFields(spec, vista?.subtitle_fields ?? []);
  const campoGrupo = vista?.group_by ?? null;
  const metaGrupo = campoGrupo === null ? undefined : spec.fields[splitGroupBy(campoGrupo).campo];

  const [movimientos, setMovimientos] = useState<Record<number, JsonValue>>({});
  const [arrastrada, setArrastrada] = useState<number | null>(null);
  const [columnaActiva, setColumnaActiva] = useState<string | null>(null);
  const [anuncio, setAnuncio] = useState('');

  const dominio = useMemo(() => andDomains(baseDomain, busqueda.domain), [baseDomain, busqueda.domain]);
  const orden = serializeOrder(parseOrder(spec.views.list?.default_order));

  const campos = useMemo(() => {
    const pedidos = new Set<string>([campoTitulo, ...subtitulos]);
    if (campoGrupo !== null) pedidos.add(splitGroupBy(campoGrupo).campo);
    return [...pedidos].filter((nombre) => spec.fields[nombre] !== undefined);
  }, [campoTitulo, subtitulos, campoGrupo, spec]);

  const tarjetas = useRecordsQuery(spec.model, {
    domain: dominio,
    limit: TOPE_TARJETAS,
    fields: campos,
    ...(orden === undefined ? {} : { order: orden }),
  });

  const grupos = useReadGroupQuery(
    spec.model,
    { domain: dominio, groupby: campoGrupo === null ? [] : [campoGrupo] },
    campoGrupo !== null,
  );

  const esqueleto = useColumnSkeleton(spec, campoGrupo);

  // Los valores de grupo: los que el modelo declara (para que las columnas
  // vacias existan) mas los que trajo `read_group` y el esqueleto no previo —
  // un estado viejo que quedo en datos tiene que verse igual, no desaparecer.
  const valoresColumna: JsonValue[] = useMemo(() => {
    const deGrupos: JsonValue[] = (grupos.data?.groups ?? []).map((grupo) =>
      campoGrupo === null ? null : (grupo[campoGrupo] ?? null),
    );
    if (esqueleto.valores === null) return deGrupos;
    const vistos = new Set(esqueleto.valores.map((valor) => groupKey(valor)));
    return [...esqueleto.valores, ...deGrupos.filter((valor) => !vistos.has(groupKey(valor)))];
  }, [grupos.data, campoGrupo, esqueleto.valores]);

  const etiquetar = useGroupLabels(spec, campoGrupo, valoresColumna);

  const mover = useMutation({
    mutationFn: (entrada: { id: number; valor: JsonValue }) =>
      api.updateRecord(spec.model, entrada.id, { [splitGroupBy(campoGrupo ?? '').campo]: entrada.valor }),
    onSuccess: () => {
      void invalidar(spec.model);
    },
    onError: (error, entrada) => {
      // Vuelta atrás: la tarjeta regresa a su columna original.
      setMovimientos((actual) => {
        const copia = { ...actual };
        delete copia[entrada.id];
        return copia;
      });
      notify.error('No se pudo mover', error instanceof Error ? error.message : undefined);
    },
  });

  const moverA = (fila: RecordData, valor: JsonValue, etiqueta: string): void => {
    if (campoGrupo === null) return;
    if (valorEfectivo(fila) === valor) return;
    setMovimientos((actual) => ({ ...actual, [fila.id]: valor }));
    setAnuncio(`${fila.display_name} se movió a ${etiqueta}.`);
    mover.mutate({ id: fila.id, valor });
  };

  const valorEfectivo = (fila: RecordData): JsonValue => {
    const local = movimientos[fila.id];
    if (local !== undefined) return local;
    return campoGrupo === null ? null : valorDeGrupo(fila, splitGroupBy(campoGrupo).campo);
  };

  const filas = tarjetas.data?.results ?? [];

  const encabezado = (
    <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
      <div className="min-w-0">
        <h1 className="text-2xl font-semibold tracking-tight">{spec.label_plural}</h1>
        <p className="mt-1 text-sm text-fg-muted">
          {campoGrupo === null
            ? 'Tablero sin columnas: el modelo no declara por qué campo agrupar.'
            : `Agrupado por ${metaGrupo?.label ?? campoGrupo}`}
        </p>
      </div>
      {/* Sin `shrink-0`: ver la nota de FormView. */}
      <div className="flex min-w-0 flex-wrap items-center gap-2 sm:justify-end">
        {modeSwitcher}
        {spec.permissions.create && (
          <Button
            variant="primary"
            onClick={() => {
              navegar(recordPath(spec.model, 'new', menuName));
            }}
          >
            Nuevo
          </Button>
        )}
      </div>
    </div>
  );

  if (tarjetas.isError) {
    return (
      <>
        {encabezado}
        <ErrorState
          title="No se pudo cargar el tablero"
          message={tarjetas.error.message}
          onRetry={() => {
            void tarjetas.refetch();
          }}
        />
      </>
    );
  }

  const puedeMover = spec.permissions.write && campoGrupo !== null && metaGrupo?.readonly !== true;

  /**
   * Por qué las tarjetas no se arrastran, o `null` si sí se arrastran.
   *
   * Sin esto, un tablero que no se puede mover se ve **igual** que uno que sí:
   * lo único que cambia es que desaparece el `cursor-grab` y el selector de
   * columna del pie de cada tarjeta. Quien vio el tablero moverse en la máquina
   * de al lado se queda intentando arrastrar contra un tablero mudo. El motivo
   * ya existía —el toast de error lo dice si el movimiento llega a intentarse
   * por otra vía—; lo que faltaba era decirlo ANTES.
   */
  const motivoSinArrastrar: string | null = puedeMover
    ? null
    : !spec.permissions.write
      ? `Tu usuario puede mirar este tablero pero no mover las tarjetas: para eso hace falta permiso para modificar ${spec.label_plural.toLowerCase()}.`
      : campoGrupo === null
        ? 'Las tarjetas no se pueden mover porque el tablero no está agrupado por ninguna columna: no hay a dónde moverlas.'
        : `«${metaGrupo?.label ?? 'La columna'}» la calcula el sistema y no se cambia a mano, así que las tarjetas no se arrastran entre columnas.`;

  // El total sale de `read_group`, que ve todas las filas; una columna del
  // esqueleto que ese resultado no menciona esta vacia de verdad, no recortada.
  const totales = new Map<string, number>(
    (grupos.data?.groups ?? []).map((grupo) => [
      groupKey(campoGrupo === null ? null : (grupo[campoGrupo] ?? null)),
      grupo.__count,
    ]),
  );

  const columnas =
    campoGrupo === null
      ? [{ valor: null as JsonValue, etiqueta: spec.label_plural, total: tarjetas.data?.count ?? 0 }]
      : valoresColumna.map((valor) => ({
          valor,
          etiqueta: etiquetar(valor),
          total: totales.get(groupKey(valor)) ?? 0,
        }));

  return (
    <>
      {encabezado}
      <SearchPanel spec={spec} estado={busqueda} mostrarAgrupacion={false} />

      <p aria-live="polite" className="sr-only">
        {anuncio}
      </p>

      {motivoSinArrastrar !== null && filas.length > 0 && (
        <div className="mb-3">
          <AvisoDeBloqueo motivo={motivoSinArrastrar} />
        </div>
      )}

      {tarjetas.isPending || (campoGrupo !== null && (grupos.isPending || esqueleto.isPending)) ? (
        <div className="flex gap-4 overflow-x-auto pb-2" aria-busy="true">
          {Array.from({ length: 3 }, (_, indice) => (
            <div key={indice} className="w-72 shrink-0 space-y-2">
              <div className="h-8 animate-pulse rounded-lg bg-surface-raised" />
              <div className="h-24 animate-pulse rounded-lg bg-surface-raised" />
              <div className="h-24 animate-pulse rounded-lg bg-surface-raised" />
            </div>
          ))}
        </div>
      ) : columnas.length === 0 || filas.length === 0 ? (
        <EmptyState
          title={busqueda.hayCriterios ? 'Ningún resultado con esos criterios' : `Todavía no hay ${spec.label_plural.toLowerCase()}`}
          description={
            busqueda.hayCriterios
              ? 'Probá con menos filtros o revisá cómo escribiste la búsqueda.'
              : // Sin artículo: la etiqueta del modelo no declara género (ver el
                // mismo caso en `ListView`).
                'Lo que cargues va a aparecer acá como tarjetas.'
          }
          action={
            busqueda.hayCriterios ? (
              <Button onClick={busqueda.limpiar}>Limpiar filtros</Button>
            ) : spec.permissions.create ? (
              <Button
                variant="primary"
                onClick={() => {
                  navegar(recordPath(spec.model, 'new', menuName));
                }}
              >
                Crear {spec.label.toLowerCase()}
              </Button>
            ) : undefined
          }
        />
      ) : (
        <div className="flex gap-4 overflow-x-auto pb-3">
          {columnas.map((columna) => {
            const clave = groupKey(columna.valor);
            const deLaColumna = filas.filter((fila) => valorEfectivo(fila) === columna.valor);

            return (
              <section
                key={clave}
                aria-label={`${columna.etiqueta}: ${columna.total} ${columna.total === 1 ? 'elemento' : 'elementos'}`}
                className={clsx(
                  'flex w-72 shrink-0 flex-col rounded-xl border bg-app-subtle p-2 transition-colors',
                  columnaActiva === clave ? 'border-accent bg-accent-soft' : 'border-line',
                )}
                onDragOver={(evento: DragEvent<HTMLElement>) => {
                  if (!puedeMover || arrastrada === null) return;
                  evento.preventDefault();
                  setColumnaActiva(clave);
                }}
                onDragLeave={() => {
                  setColumnaActiva((actual) => (actual === clave ? null : actual));
                }}
                onDrop={(evento: DragEvent<HTMLElement>) => {
                  evento.preventDefault();
                  setColumnaActiva(null);
                  if (!puedeMover || arrastrada === null) return;
                  const fila = filas.find((candidata) => candidata.id === arrastrada);
                  setArrastrada(null);
                  if (fila !== undefined) moverA(fila, columna.valor, columna.etiqueta);
                }}
              >
                <h2 className="mb-2 flex items-center justify-between gap-2 px-1 py-1 text-sm font-semibold">
                  <span className="min-w-0 truncate">{columna.etiqueta}</span>
                  <span className="shrink-0 rounded-full bg-surface px-2 py-0.5 text-xs tabular-nums text-fg-muted">
                    {columna.total}
                  </span>
                </h2>

                <ul className="flex min-h-[3rem] flex-col gap-2">
                  {deLaColumna.map((fila) => (
                    <li key={fila.id}>
                      <Tarjeta
                        spec={spec}
                        fila={fila}
                        campoTitulo={campoTitulo}
                        subtitulos={subtitulos}
                        menuName={menuName}
                        arrastrable={puedeMover}
                        columnas={columnas}
                        columnaActual={columna.valor}
                        onArrastrar={(id) => {
                          setArrastrada(id);
                        }}
                        onSoltar={() => {
                          setArrastrada(null);
                          setColumnaActiva(null);
                        }}
                        onMover={(valor, etiqueta) => {
                          moverA(fila, valor, etiqueta);
                        }}
                      />
                    </li>
                  ))}

                  {deLaColumna.length === 0 && (
                    <li className="rounded-lg border border-dashed border-line px-3 py-6 text-center text-xs text-fg-faint">
                      Sin tarjetas
                    </li>
                  )}
                </ul>

                {columna.total > deLaColumna.length && (
                  <p className="mt-2 px-1 text-xs text-fg-muted">
                    {columna.total - deLaColumna.length} más. Usá la lista para verlas todas.
                  </p>
                )}
              </section>
            );
          })}
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------
// Tarjeta
// ---------------------------------------------------------------------------
function Tarjeta({
  spec,
  fila,
  campoTitulo,
  subtitulos,
  menuName,
  arrastrable,
  columnas,
  columnaActual,
  onArrastrar,
  onSoltar,
  onMover,
}: {
  spec: ModelSpec;
  fila: RecordData;
  campoTitulo: string;
  subtitulos: string[];
  menuName: string | null;
  arrastrable: boolean;
  columnas: Array<{ valor: JsonValue; etiqueta: string }>;
  columnaActual: JsonValue;
  onArrastrar: (id: number) => void;
  onSoltar: () => void;
  onMover: (valor: JsonValue, etiqueta: string) => void;
}) {
  const metaTitulo = spec.fields[campoTitulo];

  return (
    <article
      draggable={arrastrable}
      onDragStart={(evento: DragEvent<HTMLElement>) => {
        evento.dataTransfer.effectAllowed = 'move';
        evento.dataTransfer.setData('text/plain', String(fila.id));
        onArrastrar(fila.id);
      }}
      onDragEnd={onSoltar}
      className={clsx(
        'rounded-lg border border-line bg-surface p-3 shadow-sm transition-shadow',
        arrastrable && 'cursor-grab active:cursor-grabbing hover:shadow-md',
      )}
    >
      <h3 className="text-sm font-medium">
        <Link to={recordPath(spec.model, fila.id, menuName)} className="hover:text-accent hover:underline">
          {metaTitulo === undefined ? (
            fila.display_name
          ) : (
            <FieldDisplay meta={metaTitulo} value={fila[campoTitulo] ?? null} compact />
          )}
        </Link>
      </h3>

      {subtitulos.length > 0 && (
        <dl className="mt-2 space-y-1">
          {subtitulos.map((nombre) => {
            const meta = spec.fields[nombre];
            if (meta === undefined) return null;
            const valor = fila[nombre] ?? null;
            if (valor === null || valor === '') return null;
            return (
              <div key={nombre} className="flex items-baseline gap-1.5 text-xs text-fg-muted">
                <dt className="sr-only">{meta.label}</dt>
                <dd className="min-w-0 truncate">
                  <FieldDisplay meta={meta} value={valor} compact />
                </dd>
              </div>
            );
          })}
        </dl>
      )}

      {arrastrable && columnas.length > 1 && (
        <label className="mt-3 flex items-center gap-1.5 text-2xs text-fg-faint">
          <span className="sr-only">Mover {fila.display_name} a</span>
          <span aria-hidden="true">Mover a</span>
          <select
            value={groupKey(columnaActual)}
            onChange={(evento) => {
              const destino = columnas.find((columna) => groupKey(columna.valor) === evento.target.value);
              if (destino !== undefined) onMover(destino.valor, destino.etiqueta);
            }}
            className="min-w-0 flex-1 rounded border border-line bg-surface px-1.5 py-1 text-xs"
          >
            {columnas.map((columna) => (
              <option key={groupKey(columna.valor)} value={groupKey(columna.valor)}>
                {columna.etiqueta}
              </option>
            ))}
          </select>
        </label>
      )}
    </article>
  );
}
